import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

type DownloadPdfParams = {
  element: HTMLElement;
  fileName: string;
  scale?: number;
};

export async function downloadElementAsPdf({ element, fileName, scale = 2 }: DownloadPdfParams): Promise<void> {
  const canvas = await html2canvas(element, {
    scale,
    useCORS: true,
    backgroundColor: '#ffffff',
    scrollY: -window.scrollY,
  });

  const imgData = canvas.toDataURL('image/png');

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  const imgWidth = pdfWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = 0;

  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pdfHeight;

  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;
  }

  pdf.save(fileName);
}

export type PdfNoteItem = {
  heading: string;
  body: string;
  meta?: string;
};

type DownloadNotesPdfParams = {
  title: string;
  patientId?: string;
  notes: PdfNoteItem[];
  fileName: string;
};

export function downloadNotesPdf({ title, patientId, notes, fileName }: DownloadNotesPdfParams): void {
  const pdf = new jsPDF('p', 'mm', 'a4');

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const left = 14;
  const right = 14;
  const top = 14;
  const bottom = 14;
  const contentWidth = pageWidth - left - right;

  let y = top;

  const ensurePageSpace = (neededHeight = 8) => {
    if (y + neededHeight > pageHeight - bottom) {
      pdf.addPage();
      y = top;
    }
  };

  const writeText = (
    text: string,
    options?: {
      fontSize?: number;
      fontStyle?: 'normal' | 'bold';
      indent?: number;
      lineGap?: number;
      color?: [number, number, number];
    }
  ) => {
    const fontSize = options?.fontSize ?? 11;
    const fontStyle = options?.fontStyle ?? 'normal';
    const indent = options?.indent ?? 0;
    const lineGap = options?.lineGap ?? 5;
    const color = options?.color ?? [0, 0, 0];

    pdf.setFont('helvetica', fontStyle);
    pdf.setFontSize(fontSize);
    pdf.setTextColor(color[0], color[1], color[2]);

    const safeText = (text ?? '').toString().replace(/\s+/g, ' ').trim();
    const lines = pdf.splitTextToSize(safeText || ' ', contentWidth - indent) as string[];

    lines.forEach((line: string) => {
      ensurePageSpace(lineGap);
      pdf.text(line, left + indent, y);
      y += lineGap;
    });
  };

  const writeRightText = (
    text: string,
    yPos: number,
    options?: {
      fontSize?: number;
      fontStyle?: 'normal' | 'bold';
      color?: [number, number, number];
    }
  ) => {
    const fontSize = options?.fontSize ?? 10;
    const fontStyle = options?.fontStyle ?? 'bold';
    const color = options?.color ?? [70, 70, 70];

    pdf.setFont('helvetica', fontStyle);
    pdf.setFontSize(fontSize);
    pdf.setTextColor(color[0], color[1], color[2]);

    const safeText = (text ?? '').toString().replace(/\s+/g, ' ').trim();
    const textWidth = pdf.getTextWidth(safeText);
    pdf.text(safeText, pageWidth - right - textWidth, yPos);
  };

  const drawDivider = () => {
    ensurePageSpace(4);
    pdf.setDrawColor(190, 190, 190);
    pdf.line(left, y, pageWidth - right, y);
    y += 4;
  };

  const writeGap = (gap = 4) => {
    y += gap;
    ensurePageSpace();
  };

  const drawFooter = () => {
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      pdf.text(`Page ${i} of ${pageCount}`, pageWidth - right - 18, pageHeight - 6);
    }
  };

  // Title centered
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.setTextColor(0, 70, 140);
  const safeTitle = title || 'Task Notes';
  const titleWidth = pdf.getTextWidth(safeTitle);
  pdf.text(safeTitle, (pageWidth - titleWidth) / 2, y);

  y += 8;

  // Metadata on right
  if (patientId) {
    writeRightText(`Patient ID: ${patientId}`, y, { fontSize: 10, color: [0, 70, 140] });
    y += 5;
  }

  const generatedOn = new Date().toLocaleString();
  writeRightText(`Generated On: ${generatedOn}`, y, { fontSize: 10, color: [0, 70, 140] });
  y += 5;

  writeGap(2);
  drawDivider();
  writeGap(2);

  if (!notes || notes.length === 0) {
    writeText('No notes found.', {
      fontSize: 11,
      fontStyle: 'normal',
      color: [140, 140, 140],
    });
    drawFooter();
    pdf.save(fileName);
    return;
  }

  notes.forEach((note) => {
    const heading = (note.heading || '').toString();
    const body = (note.body || '').toString();
    const meta = (note.meta || '').toString();

    writeText(heading || 'Note', {
      fontSize: 11,
      fontStyle: 'bold',
      color: [0, 90, 180],
      indent: 0,
      lineGap: 6,
    });

    writeGap(1);

    if (body) {
      writeText(body, {
        fontSize: 11,
        indent: 3,
        color: [35, 35, 35],
        lineGap: 5,
      });
    } else {
      writeText('No note content provided', {
        fontSize: 10,
        indent: 3,
        color: [140, 140, 140],
        lineGap: 5,
      });
    }

    if (meta) {
      writeGap(1);
      writeText(meta, {
        fontSize: 10,
        indent: 9,
        color: [0, 115, 55],
        lineGap: 5,
      });
    }

    writeGap(2);
    drawDivider();
    writeGap(2);
  });

  drawFooter();
  pdf.save(fileName);
}

export function sanitizePdfFileName(name: string) {
  return (name || 'download').replace(/[^a-z0-9]/gi, '_');
}
